/**
 * Canonical React Query keys for the AI Use Case Register.
 *
 * There were four different keys for two concerns before this file existed, which meant
 * invalidation silently missed subscribers:
 *
 *   ['ai-use-case', id]            wizard read + setQueryData
 *   ['aiUseCase', id]              detail page read
 *   ['ai-use-cases']               wizard invalidations — NOTHING subscribed to this key,
 *                                  so every one of them was dead code
 *   ['aiUseCases']                 dashboard + report reads
 *   ['list', '/ai-use-cases', '']  the register list, via RegisterItemList
 *
 * The consequence was invisible until a field appeared on two surfaces at once: save in the
 * wizard, and the detail page and the dashboard both went on showing the old record until a
 * hard reload. So the keys live here, one per concern, and nothing composes a key inline.
 *
 * `['aiUseCase', id]` / `['aiUseCases']` won because three of the four surfaces already used
 * them and because the camelCase resource pair matches the house convention elsewhere
 * (`['meetingMinutes']`, `['initiative', id]` / `['initiatives']`).
 *
 * `registerList` is the exception and deliberately not renamed: that key belongs to
 * `RegisterItemList`, a component shared by seven registers, which builds it as
 * `['list', listPath, registerId]`. It is quoted here as the two-element **prefix** so this
 * module never has to know what that third element is — React Query matches keys by prefix,
 * so `['list', '/ai-use-cases']` invalidates every register-id variant of it.
 */

import type { QueryClient } from '@tanstack/react-query';
import type { AiUseCase } from '../../types/models';

export const aiUseCaseKeys = {
  /** One record, by id. */
  detail: (useCaseId: string | undefined) => ['aiUseCase', useCaseId] as const,
  /** Every record's detail entry — a prefix, for invalidating them all at once. */
  details: () => ['aiUseCase'] as const,
  /** The whole register as `AiUseCase[]` (dashboard, PDF report). */
  collection: () => ['aiUseCases'] as const,
  /** RegisterItemList's own key for this register, as a prefix. Not ours to rename. */
  registerList: () => ['list', '/ai-use-cases'] as const,
} as const;

/**
 * Everything that shows this record is now stale. Call after any write — a save, a
 * transition, a delete — rather than picking keys at the call site, because "which surfaces
 * show this?" is the question that was being answered wrongly.
 *
 * Omit `useCaseId` for a write that isn't about one record (a create, a bulk import).
 */
export function invalidateAiUseCase(qc: QueryClient, useCaseId?: string): Promise<void> {
  const jobs = [invalidateAiUseCaseLists(qc)];
  if (useCaseId) jobs.push(qc.invalidateQueries({ queryKey: aiUseCaseKeys.detail(useCaseId) }));
  return Promise.all(jobs).then(() => undefined);
}

/**
 * Every surface that lists or aggregates records, but NOT any record's own detail entry.
 *
 * For the wizard, which saves on every step navigation and has the detail entry itself
 * mounted: it calls `writeAiUseCase` with the server's response first, so invalidating that
 * key would refetch a record already in hand — once per save. Callers that have just written
 * the record want this; callers that have not (a delete, a write made elsewhere) want
 * `invalidateAiUseCase`.
 */
export function invalidateAiUseCaseLists(qc: QueryClient): Promise<void> {
  return Promise.all([
    qc.invalidateQueries({ queryKey: aiUseCaseKeys.collection() }),
    qc.invalidateQueries({ queryKey: aiUseCaseKeys.registerList() }),
  ]).then(() => undefined);
}

/**
 * Seed the cache with a record the server just returned, so the next surface to mount reads
 * it without a round trip. Pairs with `invalidateAiUseCase` — this writes the record we know
 * about, that marks the lists we don't.
 */
export function writeAiUseCase(qc: QueryClient, useCase: AiUseCase): void {
  qc.setQueryData(aiUseCaseKeys.detail(useCase.useCaseId), useCase);
}
